import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Chip,
  Icons,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import CommentsEntityTypes from '@core/utils/commentsEntityTypes';
import useGetComments from '@core/comments/hooks/useGetComments';
import { getComments, getComment } from '@core/selectors/comments';
import { currentUser } from '@core/selectors/user';
import { isInstructor } from '@core/selectors/classroom';
import { AulaState } from '@core/types/state';
import formatDate from '@core/utils/formatDate';
import { UB_COMMENT_ITEM_TYPE } from '@app/utils/itemTypeForAnalytics';
import {
  ReactionList,
  ReactionPicker,
} from '@components/presentational/ResponsiveReactions';
import ResponsiveEditorReadOnly from '../Shared/ResponsiveEditorReadOnly';
import { Comment } from '../../../Comments/types';
import { createStyles } from './ResponsivePostComments.styles';

const INITIAL_VISIBLE_COMMENTS = 5;

type ResponsivePostCommentsProps = {
  postId: string;
  readOnly?: boolean;
};

const getName = (user?: Comment['user']) =>
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unknown user';

const CommentContent = ({ comment }: { comment: Comment }) => {
  if (!comment.content) return null;

  try {
    const content = JSON.parse(comment.content);
    return (
      <ResponsiveEditorReadOnly
        key={comment.editedAt || comment.id}
        itemId={comment.id}
        editorState={content}
        itemType="CommonRoom"
      />
    );
  } catch {
    return <Typography component="p">{comment.content}</Typography>;
  }
};

const CommentItem = ({
  id,
  isReply = false,
  readOnly = false,
}: {
  id: string;
  isReply?: boolean;
  readOnly?: boolean;
}) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [nameRef, isNameOverflowing] = useIsOverflowing<HTMLDivElement>();
  const comment = useSelector((state: AulaState) => getComment(state, id)) as
    | Comment
    | undefined;
  const authorIsEducator = useSelector((state: AulaState) =>
    isInstructor(state, comment?.user?.objectId)
  );

  if (!comment?.user) return null;

  const name = getName(comment.user);
  const initials =
    (comment.user.firstName?.charAt(0) || 'U').toUpperCase() +
    (comment.user.lastName?.charAt(0) || '').toUpperCase();

  return (
    <Box
      component="article"
      aria-label={`Comment by ${name}`}
      sx={styles.comment}
    >
      <Box sx={styles.commentHeader}>
        <Box sx={styles.authorRow}>
          <Box sx={styles.avatarContainer}>
            <Avatar
              altText={name}
              fallbackText={initials}
              kind="circular"
              src={comment.user.avatar}
              sx={styles.avatar}
            />
          </Box>
          <Box sx={styles.authorDetails}>
            <Box sx={styles.authorNameRow}>
              <Tooltip
                placement="top"
                title={name}
                disableFocusListener={!isNameOverflowing || !isDesktop}
                disableHoverListener={!isNameOverflowing || !isDesktop}
                disableTouchListener={!isNameOverflowing || !isDesktop}
              >
                <Box ref={nameRef} component="div" sx={styles.authorName}>
                  {name}
                </Box>
              </Tooltip>
              {authorIsEducator && (
                <Box sx={styles.educatorBadge}>
                  <Chip
                    label="Educator"
                    size="small"
                    sx={styles.educatorChip}
                  />
                </Box>
              )}
            </Box>
            <Typography sx={styles.date}>
              {formatDate(comment.createdAt)}
              {comment.editedAt ? ' (Edited)' : ''}
            </Typography>
          </Box>
        </Box>
        <Icons.MoreVert aria-hidden="true" sx={styles.moreIcon} />
      </Box>
      <Box sx={styles.commentContent}>
        <CommentContent comment={comment} />
      </Box>
      {readOnly ? (
        <ReactionList
          hideWhenNoReactions
          itemId={comment.id}
          itemType={UB_COMMENT_ITEM_TYPE}
          readOnly
          sx={{ pl: 6 }}
        />
      ) : (
        <Box aria-label="Comment reactions and actions" sx={styles.reactions}>
          <ReactionList
            hideWhenNoReactions
            itemId={comment.id}
            itemType={UB_COMMENT_ITEM_TYPE}
            readOnly={false}
          />
          <ReactionPicker itemId={comment.id} itemType={UB_COMMENT_ITEM_TYPE} />
          <Box component="span" sx={styles.reactionSpacer} />
          <Box component="span" sx={styles.replyAction}>
            <Icons.Reply aria-hidden="true" sx={styles.replyIcon} />
            <Typography sx={styles.replyLabel}>Reply</Typography>
          </Box>
        </Box>
      )}
      {!isReply && comment.replies?.length > 0 && (
        <Box sx={styles.replies}>
          {comment.replies.map((replyId) => (
            <Box key={replyId} sx={styles.reply}>
              <CommentItem id={replyId} isReply readOnly={readOnly} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const ResponsivePostComments = ({
  postId,
  readOnly = false,
}: ResponsivePostCommentsProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COMMENTS);
  const query = useGetComments(CommentsEntityTypes.post, postId);
  const comments = useSelector((state: AulaState) =>
    getComments(state, postId, CommentsEntityTypes.post)
  ) as string[];
  const user = useSelector(currentUser);
  const commentIds = query.isSuccess
    ? comments
    : query.data?.commentsIds || comments;
  const visibleIds = expanded
    ? commentIds.slice(Math.max(0, commentIds.length - visibleCount))
    : [];
  const hiddenCount = commentIds.length - visibleCount;
  const userName =
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'You';

  const toggleComments = () => setExpanded((value) => !value);

  return (
    <Box data-testid="responsive-post-comments" sx={styles.root}>
      {commentIds.length > 0 && (
        <Box
          component="button"
          onClick={toggleComments}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? 'Hide comments'
              : `Show ${commentIds.length} comment${
                  commentIds.length === 1 ? '' : 's'
                }`
          }
          sx={styles.toggleButton}
        >
          {expanded
            ? 'Hide comments'
            : `Show ${commentIds.length} comment${
                commentIds.length === 1 ? '' : 's'
              }`}
          <Icons.KeyboardArrowDown sx={styles.toggleIcon(expanded)} />
        </Box>
      )}

      {expanded && (
        <Box sx={styles.comments}>
          {hiddenCount > 0 && (
            <Box
              component="button"
              onClick={() => setVisibleCount(commentIds.length)}
              sx={styles.previousCommentsButton}
            >
              See {hiddenCount} previous comment{hiddenCount === 1 ? '' : 's'}
            </Box>
          )}
          {visibleIds.map((id, index) => (
            <Box
              key={id}
              sx={styles.commentWrapper(index === visibleIds.length - 1)}
            >
              <CommentItem id={id} readOnly={readOnly} />
            </Box>
          ))}
        </Box>
      )}

      <Box
        aria-label="Add a comment"
        sx={styles.addComment(commentIds.length > 0)}
      >
        <Avatar
          altText={userName}
          fallbackText={(user?.firstName?.charAt(0) || 'Y').toUpperCase()}
          kind="circular"
          src={user?.avatar}
          sx={styles.addCommentAvatar}
        />
        <Box sx={styles.addCommentInput}>Add a comment</Box>
      </Box>
    </Box>
  );
};

export default ResponsivePostComments;
